import React from 'react';
import { Modal, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faMobile } from '@fortawesome/free-solid-svg-icons';
import {
  ModalStatGrid,
  ModalStatItem,
  ModalStatValue,
  ModalSectionTitle,
  PlatformGrid,
  PlatformCard,
  PlatformCardTitle,
  PlatformRow,
  ModalLoadingWrap,
  ModalLoadingText,
} from './Styles';
import { MODAL_TITLE, LOADING_DETAILS, PLATFORM_BREAKDOWN_TITLE, REVENUE_LABEL, FEES_LABEL, NET_LABEL, ORDERS_LABEL } from './constants';
import type { MerchantDetailsModalProps } from './types';

const MerchantDetailsModal: React.FC<MerchantDetailsModalProps> = ({
  open,
  merchant,
  details,
  detailsLoading,
  formatCurrency,
  onClose,
}) => {
  const summary = details?.summary;
  const platform = details?.platformBreakdown;

  return (
    <Modal
      title={merchant ? MODAL_TITLE(merchant.merchantName) : ''}
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      {detailsLoading && (
        <ModalLoadingWrap>
          <Spin />
          <ModalLoadingText>{LOADING_DETAILS}</ModalLoadingText>
        </ModalLoadingWrap>
      )}
      {!detailsLoading && details && (
        <>
          <ModalStatGrid>
            <div>
              <ModalStatItem>Total Revenue</ModalStatItem>
              <ModalStatValue>{formatCurrency(summary?.totalRevenue ?? 0)}</ModalStatValue>
            </div>
            <div>
              <ModalStatItem>Total Fees</ModalStatItem>
              <ModalStatValue>{formatCurrency(summary?.totalFees ?? 0)}</ModalStatValue>
            </div>
            <div>
              <ModalStatItem>Net Earnings</ModalStatItem>
              <ModalStatValue>{formatCurrency(summary?.netEarnings ?? 0)}</ModalStatValue>
            </div>
            <div>
              <ModalStatItem>Order Count</ModalStatItem>
              <ModalStatValue>{(summary?.orderCount ?? 0).toLocaleString()}</ModalStatValue>
            </div>
          </ModalStatGrid>
          <ModalSectionTitle>{PLATFORM_BREAKDOWN_TITLE}</ModalSectionTitle>
          <PlatformGrid>
            <PlatformCard $variant="kiosk">
              <PlatformCardTitle>
                <span><FontAwesomeIcon icon={faDesktop} /> Kiosk</span>
                <span>{ORDERS_LABEL(platform?.kiosk?.orderCount ?? 0)}</span>
              </PlatformCardTitle>
              <PlatformRow>
                <span>{REVENUE_LABEL}</span>
                <span>{formatCurrency(platform?.kiosk?.revenue ?? 0)}</span>
              </PlatformRow>
              <PlatformRow>
                <span>{FEES_LABEL}</span>
                <span>{formatCurrency(platform?.kiosk?.fees ?? 0)}</span>
              </PlatformRow>
              <PlatformRow>
                <span>{NET_LABEL}</span>
                <span>{formatCurrency(platform?.kiosk?.netEarnings ?? 0)}</span>
              </PlatformRow>
            </PlatformCard>
            <PlatformCard $variant="mobile">
              <PlatformCardTitle>
                <span><FontAwesomeIcon icon={faMobile} /> Mobile</span>
                <span>{ORDERS_LABEL(platform?.mobile?.orderCount ?? 0)}</span>
              </PlatformCardTitle>
              <PlatformRow>
                <span>{REVENUE_LABEL}</span>
                <span>{formatCurrency(platform?.mobile?.revenue ?? 0)}</span>
              </PlatformRow>
              <PlatformRow>
                <span>{FEES_LABEL}</span>
                <span>{formatCurrency(platform?.mobile?.fees ?? 0)}</span>
              </PlatformRow>
              <PlatformRow>
                <span>{NET_LABEL}</span>
                <span>{formatCurrency(platform?.mobile?.netEarnings ?? 0)}</span>
              </PlatformRow>
            </PlatformCard>
          </PlatformGrid>
        </>
      )}
    </Modal>
  );
};

export default React.memo(MerchantDetailsModal);
